import { useState, useCallback, useEffect } from 'react';
import { useError } from '@app/hooks/useError';
import Logger from '@app/utils/logger';
import { FileService } from '@app/api/files';
import { FileEntry } from '@app/api/files/types';

// Define sort order options
export type SortOrder = 'asc' | 'desc';

export interface FileBrowserState {
  contents: FileEntry[];
  currentPath: string;
  isLoading: boolean;
  error: string | null;
  navigationHistory: string[];
  historyIndex: number;
  directoryCache: Record<string, FileEntry[]>;
  sortOrder: SortOrder;
}

export function useFileBrowser(initialPath = '') {
  const [state, setState] = useState<FileBrowserState>({
    contents: [],
    currentPath: initialPath,
    isLoading: false,
    error: null,
    navigationHistory: ['/'],
    historyIndex: 0,
    directoryCache: {},
    sortOrder: 'asc',
  });

  const { showError } = useError();

  // Navigation state derived from state
  const canGoBack = state.historyIndex > 0;
  const canGoForward = state.historyIndex < state.navigationHistory.length - 1;

  // Normalize path to ensure consistent format for caching and comparison
  const normalizePath = useCallback((path: string): string => {
    // Ensure path starts with '/'
    let normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Remove trailing slash unless it's the root path
    if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    return normalizedPath;
  }, []);

  // Function to extract numerical prefix from a filename
  const extractNumber = useCallback((filename: string): [number | null, string] => {
    const match = filename.match(/^(\d+)(.*)$/);
    if (match) {
      return [parseInt(match[1], 10), match[2]];
    }
    return [null, filename];
  }, []);

  // Sort entries with directories first and respecting numerical prefixes
  const sortEntries = useCallback(
    (entries: FileEntry[], order: SortOrder): FileEntry[] => {
      // Clone the array to avoid mutating the original
      const sorted = [...entries];

      return sorted.sort((a, b) => {
        // Always put directories before files
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1;
        }

        // Extract potential numerical prefixes
        const [numA, restA] = extractNumber(a.name);
        const [numB, restB] = extractNumber(b.name);

        // If both have numerical prefixes, compare them numerically
        if (numA !== null && numB !== null) {
          if (numA !== numB) {
            return order === 'asc' ? numA - numB : numB - numA;
          }
          // If numbers are equal, compare the rest
          return order === 'asc' ? restA.localeCompare(restB) : restB.localeCompare(restA);
        }

        // If only one has a numerical prefix, put it first in asc order
        if (numA !== null) return order === 'asc' ? -1 : 1;
        if (numB !== null) return order === 'asc' ? 1 : -1;

        // Neither has a numerical prefix, do a regular string comparison
        return order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      });
    },
    [extractNumber],
  );

  // Toggle between asc and desc sort orders
  const cycleSortOrder = useCallback(() => {
    setState((prev) => ({
      ...prev,
      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // Set a specific sort order
  const setSortOrder = useCallback((order: SortOrder) => {
    setState((prev) => ({
      ...prev,
      sortOrder: order,
    }));
  }, []);

  // Fetch directory contents from API
  const fetchDirectory = useCallback(
    async (path: string, forceRefresh = false) => {
      const normalizedPath = normalizePath(path);

      // Use cached data if available and not forcing refresh
      if (!forceRefresh && state.directoryCache[normalizedPath]) {
        setState((prev) => ({
          ...prev,
          contents: state.directoryCache[normalizedPath],
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      try {
        const data = await FileService.browseDirectory(normalizedPath);

        if (data && data.contents) {
          // Update cache with new data
          setState((prev) => ({
            ...prev,
            contents: data.contents,
            isLoading: false,
            directoryCache: {
              ...prev.directoryCache,
              [normalizedPath]: data.contents,
            },
          }));
        } else {
          setState((prev) => ({
            ...prev,
            contents: [],
            isLoading: false,
          }));
        }
      } catch (err) {
        Logger.error('Error fetching directory:', err);
        showError({
          code: 'FILE_BROWSER_ERROR',
          message: 'Failed to fetch directory contents',
          severity: 'error',
          details: { path: normalizedPath, error: err },
          suggestion: 'Please try refreshing the page or logging in again.',
        });
        setState((prev) => ({
          ...prev,
          isLoading: false,
          contents: [],
          error: err instanceof Error ? err.message : 'Error fetching directory contents',
        }));
      }
    },
    [state.directoryCache, showError, normalizePath],
  );

  // Check if a path is inside a .zarr directory
  const isInsideZarrDirectory = useCallback(
    (path: string): boolean => {
      const segments = normalizePath(path).split('/').filter(Boolean);

      // Look for any .zarr segments except the last one
      for (let i = 0; i < segments.length - 1; i++) {
        if (segments[i].endsWith('.zarr')) {
          return true;
        }
      }

      return false;
    },
    [normalizePath],
  );

  // Check if a directory should be browsable
  const isDirectoryBrowsable = useCallback(
    (dirPath: string): boolean => {
      // Allow clicking on .zarr directories themselves, but not their subdirectories
      return !isInsideZarrDirectory(dirPath);
    },
    [isInsideZarrDirectory],
  );

  // Navigate to a different directory
  const navigateTo = useCallback(
    (path: string) => {
      const normalizedPath = normalizePath(path);
      const normalizedCurrentPath = normalizePath(state.currentPath);

      // Prevent redundant navigation
      if (normalizedPath !== normalizedCurrentPath) {
        setState((prev) => {
          const newHistory = [
            ...prev.navigationHistory.slice(0, prev.historyIndex + 1),
            normalizedPath,
          ];
          return {
            ...prev,
            currentPath: normalizedPath,
            navigationHistory: newHistory,
            historyIndex: newHistory.length - 1,
          };
        });

        fetchDirectory(normalizedPath);
      }
    },
    [state.currentPath, fetchDirectory, normalizePath],
  );

  // Navigate back in history
  const goBack = useCallback(() => {
    if (canGoBack) {
      const prevPath = state.navigationHistory[state.historyIndex - 1];
      const normalizedPath = normalizePath(prevPath);

      setState((prev) => ({
        ...prev,
        currentPath: normalizedPath,
        historyIndex: prev.historyIndex - 1,
      }));

      fetchDirectory(normalizedPath);
    }
  }, [canGoBack, state.navigationHistory, state.historyIndex, fetchDirectory, normalizePath]);

  // Navigate forward in history
  const goForward = useCallback(() => {
    if (canGoForward) {
      const nextPath = state.navigationHistory[state.historyIndex + 1];
      const normalizedPath = normalizePath(nextPath);

      setState((prev) => ({
        ...prev,
        currentPath: normalizedPath,
        historyIndex: prev.historyIndex + 1,
      }));

      fetchDirectory(normalizedPath);
    }
  }, [canGoForward, state.navigationHistory, state.historyIndex, fetchDirectory, normalizePath]);

  // Get parent directory
  const getParentDirectory = useCallback(
    (path: string): string => {
      const normalizedPath = normalizePath(path);

      // If we're at root, stay at root
      if (normalizedPath === '/') return '/';

      // Split the path and remove the last segment
      const segments = normalizedPath.split('/').filter(Boolean);
      segments.pop();

      // If no segments left, go to root
      if (segments.length === 0) return '/';

      // Reconstruct the path
      return '/' + segments.join('/');
    },
    [normalizePath],
  );

  // Refresh current directory
  const refreshDirectory = useCallback(() => {
    const normalizedPath = normalizePath(state.currentPath);
    fetchDirectory(normalizedPath, true);
  }, [state.currentPath, fetchDirectory, normalizePath]);

  // Get sorted contents based on current sort order
  const sortedContents = useCallback(() => {
    return sortEntries(state.contents, state.sortOrder);
  }, [state.contents, state.sortOrder, sortEntries]);

  // Auto-fetch initial directory on mount
  useEffect(() => {
    const initialPathToFetch = initialPath || '/';
    fetchDirectory(initialPathToFetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs only once on mount

  return {
    ...state,
    contents: sortedContents(),
    canGoBack,
    canGoForward,
    fetchDirectory,
    navigateTo,
    goBack,
    goForward,
    getParentDirectory,
    isInsideZarrDirectory,
    isDirectoryBrowsable,
    refreshDirectory,
    cycleSortOrder,
    setSortOrder,
  };
}
